import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { ShieldCheck, UserPlus, Trash2, Bell, ArrowLeft } from 'lucide-react';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth/settings/admins')({ component: PlatformAdminsPage });

interface PlatformAdmin {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  role: string;
  receiveAlerts: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export default function PlatformAdminsPage() {
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', receiveAlerts: false });
  const [removingId, setRemovingId] = useState<string | null>(null);

  const { data: admins = [], isLoading } = useQuery<PlatformAdmin[]>({
    queryKey: ['platform-admins'],
    queryFn: () => adminApi.platformAdmins.list() as Promise<PlatformAdmin[]>,
  });

  const { mutate: invite, isPending: inviting } = useMutation({
    mutationFn: () => adminApi.platformAdmins.invite(form),
    onSuccess: () => {
      toast.success(`${form.email} invited as platform admin`);
      setForm({ firstName: '', lastName: '', email: '', receiveAlerts: false });
      setShowInvite(false);
      qc.invalidateQueries({ queryKey: ['platform-admins'] });
    },
    onError: (e: Error) => toast.error(e?.message ?? 'Failed to invite'),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: string) => adminApi.platformAdmins.remove(id),
    onSuccess: () => {
      toast.success('Admin removed');
      setRemovingId(null);
      qc.invalidateQueries({ queryKey: ['platform-admins'] });
    },
    onError: (e: Error) => { toast.error(e?.message ?? 'Failed to remove'); setRemovingId(null); },
  });

  const canSubmit = form.firstName.trim() && form.lastName.trim() && form.email.trim();

  return (
    <div className="page">
      {/* Back */}
      <Link to="/settings" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: '#64748b', fontSize: 13, marginBottom: 24, textDecoration: 'none' }}>
        <ArrowLeft size={14} /> Back to Settings
      </Link>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(99,102,241,0.12)', color: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldCheck size={20} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Platform Admins</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#475569' }}>
              {admins.length} super admin{admins.length !== 1 ? 's' : ''} with full platform access
            </p>
          </div>
        </div>
        <button
          id="invite-admin-btn"
          onClick={() => setShowInvite(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: showInvite ? 'var(--color-surface-2)' : 'var(--color-primary)',
            border: '1px solid ' + (showInvite ? 'var(--color-border)' : 'transparent'),
            color: showInvite ? 'var(--color-text-3)' : '#fff', cursor: 'pointer',
          }}
        >
          <UserPlus size={15} /> {showInvite ? 'Cancel' : 'Invite admin'}
        </button>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '20px', marginBottom: 24 }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>New platform admin</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>First name *</label>
              <input id="invite-first-name" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Henri" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Last name *</label>
              <input id="invite-last-name" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Hovi" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Work email *</label>
            <input id="invite-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@company.com" style={{ ...inputStyle, width: '100%' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', marginBottom: 18 }}>
            <input id="invite-receive-alerts" type="checkbox" checked={form.receiveAlerts} onChange={e => setForm(f => ({ ...f, receiveAlerts: e.target.checked }))} style={{ width: 15, height: 15, accentColor: 'var(--color-primary)', cursor: 'pointer' }} />
            <span style={{ fontSize: 13, color: '#94a3b8' }}>Receive platform alert notifications</span>
          </label>
          <button
            id="invite-submit-btn"
            onClick={() => invite()}
            disabled={!canSubmit || inviting}
            style={{ padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'var(--color-primary)', border: 'none', color: '#fff', cursor: canSubmit && !inviting ? 'pointer' : 'not-allowed', opacity: canSubmit && !inviting ? 1 : 0.5, display: 'flex', alignItems: 'center', gap: 7 }}
          >
            {inviting ? <Spinner size="sm" /> : null}
            {inviting ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      )}

      {/* Admin list */}
      {isLoading ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner size="md" /></div>
      ) : (
        <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
          {admins.map((admin, i) => (
            <div key={admin.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: i < admins.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
              {/* Avatar */}
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {(admin.firstName?.[0] ?? admin.email[0]).toUpperCase()}
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>
                  {admin.firstName && admin.lastName ? `${admin.firstName} ${admin.lastName}` : admin.name ?? admin.email}
                </p>
                <p style={{ margin: '1px 0 0', fontSize: 12, color: '#475569' }}>{admin.email}</p>
              </div>

              {/* Badges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#6366f120', color: '#818cf8', border: '1px solid #6366f130', letterSpacing: '0.04em' }}>
                  SUPER ADMIN
                </span>
                {admin.receiveAlerts && (
                  <span title="Receives platform alerts" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#10b98115', color: '#34d399', border: '1px solid #10b98130' }}>
                    <Bell size={10} /> Alerts
                  </span>
                )}
                <span style={{ fontSize: 12, color: '#334155' }}>
                  {admin.lastLoginAt ? `Last login ${new Date(admin.lastLoginAt).toLocaleDateString()}` : 'Never logged in'}
                </span>
              </div>

              {/* Remove — only if not last */}
              {admins.length > 1 && (
                removingId === admin.id ? (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => remove(admin.id)} style={{ padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: '#ef444420', border: '1px solid #ef444440', color: '#f87171', cursor: 'pointer' }}>Confirm</button>
                    <button onClick={() => setRemovingId(null)} style={{ padding: '5px 10px', borderRadius: 7, fontSize: 12, background: 'transparent', border: '1px solid var(--color-border)', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setRemovingId(admin.id)}
                    title="Remove admin"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, background: 'transparent', border: '1px solid transparent', color: '#475569', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#ef444415'; (e.currentTarget as HTMLButtonElement).style.color = '#f87171'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#475569'; }}
                  >
                    <Trash2 size={14} />
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 5 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0a0e1c', border: '1px solid #1a1f35', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
